import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
  DefaultSidebar,
  Sidebar,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  clearAppStateForDatabase,
  getDefaultAppState,
} from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import { usersIcon, share } from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import { parseLibraryTokensFromUrl } from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { QuickBoardsTab } from "./components/QuickBoardsTab";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import { LoginScreen } from "./components/LoginScreen";
import { BoardList } from "./components/BoardList";
import { MiBoardDialog } from "./components/MiBoardDialog";
import { LoadingClock } from "./components/LoadingClock";
import { ProfilePage } from "./components/ProfilePage";
import { AdminPage } from "./components/AdminPage";
import { LandingPage } from "./components/LandingPage";
import { SignupPage } from "./components/SignupPage";
import { PasswordRecoveryPage } from "./components/PasswordRecoveryPage";
import { CheckoutPage } from "./components/CheckoutPage";
import { MiBoardStatusPage } from "./components/MiBoardStatusPage";
import { LegalPage } from "./components/LegalPage";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";
import {
  BoardLimitError,
  createBoard,
  loadBoard,
  renameBoard,
  saveBoard,
} from "./data/boards";
import { getAuthToken, getCurrentUser } from "./data/auth";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import { LocalData, localStorageQuotaExceededAtom } from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
  isCollabDisabled: boolean;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | {
        isExternalScene: true;
        id: string;
        key: string;
        boardTitle?: string;
      }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const boardId = searchParams.get("board");
  const isNewBoard = searchParams.get("new") === "1";
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  // A new board must not restore the previous local editor scene. The saved
  // board remains intact; this only starts a fresh unsaved canvas.
  const localDataState = isNewBoard ? null : importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isDisabledCollaborationLink = opts.isCollabDisabled && roomLinkData;
  if (isDisabledCollaborationLink) {
    roomLinkData = null;
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    scene.appState = {
      ...scene.appState,
      errorMessage: "A colaboração por link está desativada.",
    };
  }
  const isExternalScene = !!(id || boardId || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // internal boards are explicitly selected from the user's workspace
      boardId ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (boardId) {
        try {
          const board = await loadBoard(boardId);
          scene = {
            elements: restoreElements(board.document.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            appState: restoreAppState(
              {
                ...board.document.appState,
                showWelcomeScreen: false,
              },
              localDataState?.appState,
            ),
          };
          return {
            scene,
            isExternalScene: true,
            id: boardId,
            key: "",
            boardTitle: board.title,
          };
        } catch (error) {
          console.error("Unable to load board", error);
          return {
            scene: {
              appState: { errorMessage: "Não foi possível carregar o painel." },
            },
            isExternalScene: false,
          };
        }
      } else if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData && !boardId) {
        window.history.replaceState({}, APP_NAME, "/app");
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, "/app");
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, "/app");

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  // The canvas background is document state, separate from Excalidraw's UI
  // theme. MiBoard is intentionally light-only, including existing panels
  // that may have been saved while the previous dark appearance was active.
  if (!roomLinkData) {
    scene.appState = {
      ...scene.appState,
      viewBackgroundColor: "#ffffff",
    };
  }

  if (isNewBoard) {
    window.history.replaceState({}, APP_NAME, "/app");
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const isCollabDisabled =
    isRunningInIframe() ||
    (!isTestEnv() && import.meta.env.VITE_APP_ENABLE_COLLABORATION !== "true");
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isNewBoardDialogOpen, setIsNewBoardDialogOpen] = useState(false);
  const [boardTitle, setBoardTitle] = useState("Painel sem título");
  const [currentBoardTitle, setCurrentBoardTitle] = useState("");
  const [isSavingBoard, setIsSavingBoard] = useState(false);
  const [boardHasPendingChanges, setBoardHasPendingChanges] = useState(false);
  const [isRenameBoardDialogOpen, setIsRenameBoardDialogOpen] = useState(false);
  const [renameBoardTitle, setRenameBoardTitle] = useState("");
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const boardSaveVersionRef = useRef(0);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("miboard-app-loading");
    return () => {
      root.classList.remove("miboard-app-loading");
    };
  }, []);

  useEffect(() => {
    if (!isEditorLoading) {
      document.documentElement.classList.remove("miboard-app-loading");
    }
  }, [isEditorLoading]);

  useEffect(() => {
    getCurrentUser()
      .then((currentUser) => setIsAdmin(currentUser.profile.role === "admin"))
      .catch(() => setIsAdmin(false));
  }, []);

  const persistBoard = useRef(
    debounce(
      async (
        id: string,
        document: Parameters<typeof saveBoard>[1],
        version: number,
      ) => {
        try {
          await saveBoard(id, document);
          if (version === boardSaveVersionRef.current) {
            setBoardHasPendingChanges(false);
          }
        } catch (error) {
          console.error("Unable to save board", error);
          setBoardHasPendingChanges(true);
        }
      },
      1_000,
    ),
  ).current;

  const { editorTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  const isInitialSceneReadyRef = useRef(false);
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return !isCollabDisabled && isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    isInitialSceneReadyRef.current = false;
    setIsEditorLoading(true);
    initializeScene({ collabAPI, excalidrawAPI, isCollabDisabled }).then(
      async (data) => {
        if ("boardTitle" in data && data.boardTitle) {
          setCurrentBoardTitle(data.boardTitle);
        }
        loadImages(data, /* isInitialLoad */ true);
        isInitialSceneReadyRef.current = true;
        initialStatePromiseRef.current.promise.resolve(data.scene);
        setIsEditorLoading(false);
      },
    );

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });
        isInitialSceneReadyRef.current = false;
        setIsEditorLoading(true);

        initializeScene({ collabAPI, excalidrawAPI, isCollabDisabled }).then(
          (data) => {
            if ("boardTitle" in data && data.boardTitle) {
              setCurrentBoardTitle(data.boardTitle);
            }
            loadImages(data);
            if (data.scene) {
              excalidrawAPI.updateScene({
                elements: restoreElements(data.scene.elements, null, {
                  repairBindings: true,
                }),
                appState: restoreAppState(data.scene.appState, null),
                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
              });
            }
            isInitialSceneReadyRef.current = true;
            setIsEditorLoading(false);
          },
        );
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      // Server-backed MiBoard panels are scoped by their board id. Do not
      // synchronize the generic Excalidraw localStorage scene between tabs,
      // otherwise opening a second panel can replace the first panel's scene
      // with its own (often empty) canvas.
      const scopedToBoard = new URLSearchParams(window.location.search).has(
        "board",
      );
      const isNewBoard =
        new URLSearchParams(window.location.search).get("new") === "1";
      if (scopedToBoard || isNewBoard) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    const activeBoardId = new URLSearchParams(window.location.search).get(
      "board",
    );
    const isNewBoard =
      new URLSearchParams(window.location.search).get("new") === "1";
    // Excalidraw emits an empty onChange while async initialData is loading.
    // Do not let that event overwrite a board fetched from the API.
    if (activeBoardId && isInitialSceneReadyRef.current) {
      boardSaveVersionRef.current += 1;
      setBoardHasPendingChanges(true);
      persistBoard(
        activeBoardId,
        {
          elements,
          appState: {
            ...clearAppStateForDatabase(appState),
            showWelcomeScreen: false,
          },
        },
        boardSaveVersionRef.current,
      );
    }

    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!activeBoardId && !isNewBoard && !LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const buildPersistentDocument = () => {
    if (!excalidrawAPI) {
      return null;
    }
    return {
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      appState: {
        ...clearAppStateForDatabase(excalidrawAPI.getAppState()),
        showWelcomeScreen: false,
      },
    };
  };

  const buildEmptyDocument = () => ({
    elements: [],
    appState: {
      ...clearAppStateForDatabase(getDefaultAppState()),
      showWelcomeScreen: false,
    },
  });

  const openNewBoardDialog = () => {
    setBoardTitle("Novo painel");
    setIsNewBoardDialogOpen(true);
  };

  const openSaveBoardDialog = () => {
    setBoardTitle("Painel sem título");
    setIsSaveDialogOpen(true);
  };

  const activeBoardId = new URLSearchParams(window.location.search).get(
    "board",
  );

  const openRenameBoardDialog = () => {
    if (!activeBoardId) {
      return;
    }
    setRenameBoardTitle(currentBoardTitle || "Painel sem título");
    setIsRenameBoardDialogOpen(true);
  };

  const saveButtonLabel = activeBoardId
    ? isSavingBoard
      ? "Salvando..."
      : boardHasPendingChanges
      ? "Salvar"
      : "Salvo"
    : isSavingBoard
    ? "Salvando..."
    : "Salvar";

  const submitRenameBoard = async () => {
    if (!activeBoardId) {
      return;
    }
    const title = renameBoardTitle.trim();
    if (!title) {
      setErrorMessage("O nome do painel não pode ficar em branco.");
      return;
    }
    setIsSavingBoard(true);
    try {
      const updated = await renameBoard(activeBoardId, title);
      setCurrentBoardTitle(updated.title);
      setIsRenameBoardDialogOpen(false);
      setErrorMessage("");
    } catch (error) {
      console.error("Unable to rename board", error);
      setErrorMessage("Não foi possível renomear o painel.");
    } finally {
      setIsSavingBoard(false);
    }
  };

  const submitPersistentBoard = async () => {
    if (!excalidrawAPI) {
      return;
    }
    const activeBoardId = new URLSearchParams(window.location.search).get(
      "board",
    );
    if (!activeBoardId && !boardTitle.trim()) {
      return;
    }
    setIsSavingBoard(true);
    try {
      const document = buildPersistentDocument();
      if (!document) {
        return;
      }
      if (activeBoardId) {
        await saveBoard(activeBoardId, document);
        boardSaveVersionRef.current = 0;
        setBoardHasPendingChanges(false);
        setIsSaveDialogOpen(false);
      } else {
        const board = await createBoard({ title: boardTitle.trim(), document });
        boardSaveVersionRef.current = 0;
        setBoardHasPendingChanges(false);
        window.location.assign(`/app?board=${board.id}`);
      }
    } catch (error) {
      console.error("Unable to create board", error);
      setErrorMessage(
        error instanceof BoardLimitError
          ? error.message
          : "Não foi possível criar o painel.",
      );
    } finally {
      setIsSavingBoard(false);
    }
  };

  const submitNewBoard = async () => {
    const title = boardTitle.trim() || "Novo painel";
    setIsSavingBoard(true);
    try {
      const board = await createBoard({
        title,
        document: buildEmptyDocument(),
      });
      boardSaveVersionRef.current = 0;
      setBoardHasPendingChanges(false);
      setIsNewBoardDialogOpen(false);
      window.location.assign(`/app?board=${board.id}`);
    } catch (error) {
      console.error("Unable to create board", error);
      setErrorMessage(
        error instanceof BoardLimitError
          ? error.message
          : "Não foi possível criar o novo painel.",
      );
    } finally {
      setIsSavingBoard(false);
    }
  };

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%", position: "relative" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      {isEditorLoading && (
        <div className="miboard-editor-loading-overlay">
          <LoadingClock label="Carregando..." />
        </div>
      )}
      {currentBoardTitle && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 18,
            left: 84,
            zIndex: 6,
            pointerEvents: "none",
            userSelect: "none",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: "#05A8FF",
            opacity: 0.2,
            textShadow: "0 1px 0 rgba(255,255,255,0.35)",
          }}
        >
          {currentBoardTitle}
        </div>
      )}
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: false,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        onThemeChange={setAppTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              <button
                type="button"
                className="miboard-save-button miboard-boards-button"
                onClick={openNewBoardDialog}
              >
                Novo
              </button>
              <button
                type="button"
                className="miboard-save-button"
                onClick={openSaveBoardDialog}
              >
                {saveButtonLabel}
              </button>
              {activeBoardId && (
                <button
                  type="button"
                  className="miboard-save-button miboard-boards-button"
                  onClick={openRenameBoardDialog}
                >
                  Renomear
                </button>
              )}
              {collabAPI && !isCollabDisabled && (
                <>
                  {collabError.message && (
                    <CollabError collabError={collabError} />
                  )}
                  <LiveCollaborationTrigger
                    isCollaborating={isCollaborating}
                    onSelect={() =>
                      setShareDialogState({ isOpen: true, type: "share" })
                    }
                    editorInterface={editorInterface}
                  />
                </>
              )}
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.setViewport({
              target: element.link,
              fit: "scale-down",
              animation: true,
            });
          }
        }}
      >
        <DefaultSidebar>
          <DefaultSidebar.TabTriggers>
            <Sidebar.TabTrigger tab="boards" title="Meus painéis">
              ▦
            </Sidebar.TabTrigger>
            {isAdmin && (
              <Sidebar.TabTrigger tab="admin" title="Administração">
                ⚙
              </Sidebar.TabTrigger>
            )}
          </DefaultSidebar.TabTriggers>
          <Sidebar.Tab tab="boards">
            <QuickBoardsTab />
          </Sidebar.Tab>
          {isAdmin && (
            <Sidebar.Tab tab="admin">
              <div style={{ padding: 20 }}>
                <h2 style={{ marginTop: 0 }}>Administração</h2>
                <p>Gerencie vendas, usuários e configurações do sistema.</p>
                <a href="/app/admin">Abrir painel administrativo</a>
              </div>
            </Sidebar.Tab>
          )}
        </DefaultSidebar>
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          refresh={() => forceRefresh((prev) => !prev)}
          onCreatePersistentBoard={openSaveBoardDialog}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        {isSaveDialogOpen && (
          <MiBoardDialog
            title="Salvar painel"
            onClose={() => !isSavingBoard && setIsSaveDialogOpen(false)}
            actions={
              <>
                <button
                  type="button"
                  disabled={isSavingBoard}
                  onClick={() => setIsSaveDialogOpen(false)}
                  className="miboard-cancel-button"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isSavingBoard}
                  onClick={submitPersistentBoard}
                  className="miboard-save-button"
                >
                  {isSavingBoard ? "Salvando..." : "Salvar"}
                </button>
              </>
            }
          >
            {new URLSearchParams(window.location.search).get("board") ? (
              <p style={{ margin: 0 }}>
                As alterações deste painel serão salvas no MiBoard.
              </p>
            ) : (
              <label style={{ display: "grid", gap: 8 }}>
                Nome do painel
                <input
                  autoFocus
                  value={boardTitle}
                  onChange={(event) => setBoardTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      submitPersistentBoard();
                    }
                  }}
                  style={{
                    border: "1px solid #9ccfdf",
                    borderRadius: 8,
                    font: "inherit",
                    padding: 10,
                  }}
                />
              </label>
            )}
          </MiBoardDialog>
        )}
        {isNewBoardDialogOpen && (
          <MiBoardDialog
            title="Criar novo painel"
            onClose={() => setIsNewBoardDialogOpen(false)}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => setIsNewBoardDialogOpen(false)}
                  className="miboard-cancel-button"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="miboard-save-button"
                  disabled={isSavingBoard}
                  onClick={submitNewBoard}
                >
                  {isSavingBoard ? "Criando..." : "Criar painel"}
                </button>
              </>
            }
          >
            <p style={{ margin: 0 }}>
              Um novo painel será criado vazio e salvo no banco com o nome que
              você informar.
            </p>
            <label style={{ display: "grid", gap: 8, marginTop: 16 }}>
              Nome do painel
              <input
                autoFocus
                value={boardTitle}
                onChange={(event) => setBoardTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    submitNewBoard();
                  }
                }}
                style={{
                  border: "1px solid #9ccfdf",
                  borderRadius: 8,
                  font: "inherit",
                  padding: 10,
                }}
              />
            </label>
          </MiBoardDialog>
        )}
        {isRenameBoardDialogOpen && (
          <MiBoardDialog
            title="Renomear painel"
            onClose={() => !isSavingBoard && setIsRenameBoardDialogOpen(false)}
            actions={
              <>
                <button
                  type="button"
                  disabled={isSavingBoard}
                  onClick={() => setIsRenameBoardDialogOpen(false)}
                  className="miboard-cancel-button"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isSavingBoard}
                  onClick={submitRenameBoard}
                  className="miboard-save-button"
                >
                  {isSavingBoard ? "Salvando..." : "Salvar"}
                </button>
              </>
            }
          >
            <label style={{ display: "grid", gap: 8 }}>
              Nome do painel
              <input
                autoFocus
                value={renameBoardTitle}
                onChange={(event) => setRenameBoardTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    submitRenameBoard();
                  }
                }}
                style={{
                  border: "1px solid #9ccfdf",
                  borderRadius: 8,
                  font: "inherit",
                  padding: 10,
                }}
              />
            </label>
          </MiBoardDialog>
        )}
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !isCollabDisabled,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () =>
                !isCollabDisabled && !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAuthToken());
  const [currentRole, setCurrentRole] = useState<"admin" | "member" | null>(
    null,
  );
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentRole(null);
      return;
    }
    let alive = true;
    getCurrentUser()
      .then((currentUser) => {
        if (alive) {
          setCurrentRole(currentUser.profile.role);
        }
      })
      .catch(() => {
        if (alive) {
          setCurrentRole(null);
          setIsAuthenticated(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);
  const path = window.location.pathname;
  const url = new URL(window.location.href);
  const loginHrefFor = (nextHref: string) =>
    `/app/login?next=${encodeURIComponent(nextHref)}`;
  const isKnownAppRoute =
    path === "/app" ||
    path === "/app/boards" ||
    path === "/app/profile" ||
    path === "/app/login" ||
    path === "/app/signup" ||
    path === "/app/forgot-password" ||
    path === "/app/checkout" ||
    path === "/app/checkout/success" ||
    path === "/app/checkout/pix" ||
    path === "/app/checkout/nao-realizado" ||
    path.startsWith("/app/admin");
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  if (path === "/") {
    return <LandingPage />;
  }

  if (path === "/terms") {
    return <LegalPage kind="terms" />;
  }

  if (path === "/privacy") {
    return <LegalPage kind="privacy" />;
  }

  if (!path.startsWith("/app")) {
    window.history.replaceState({}, "", `/app${path}${window.location.search}`);
    window.location.reload();
    return null;
  }

  if (
    isAuthenticated &&
    (path === "/app/login" ||
      path === "/app/signup" ||
      path === "/app/forgot-password")
  ) {
    window.location.assign(url.searchParams.get("next") || "/app");
    return null;
  }

  if (path === "/app/checkout/success") {
    return (
      <MiBoardStatusPage
        code="Pago"
        title="Pagamento concluído com sucesso"
        description="Recebemos o retorno da jornada e seu acesso será sincronizado assim que a confirmação financeira chegar do Asaas."
        primaryLabel="Ir para meus painéis"
        primaryHref="/app/boards"
        secondaryLabel="Abrir perfil"
        secondaryHref="/app/profile"
      />
    );
  }

  if (path === "/app/checkout") {
    return <CheckoutPage />;
  }

  if (path === "/app/checkout/pix") {
    return (
      <MiBoardStatusPage
        code="Pix"
        title="Pagamento via Pix em andamento"
        description="Se você escolheu Pix, siga as instruções do checkout para concluir o pagamento. Depois da compensação, liberamos o acesso automaticamente."
        primaryLabel="Voltar para os planos"
        primaryHref="/app/profile"
        secondaryLabel="Ir para meus painéis"
        secondaryHref="/app/boards"
      />
    );
  }

  if (path === "/app/checkout/nao-realizado") {
    const reason = url.searchParams.get("reason");
    return (
      <MiBoardStatusPage
        code="Atenção"
        title="Pagamento não realizado"
        description={
          reason === "expired"
            ? "O link do checkout expirou antes da finalização. Você pode gerar um novo checkout e tentar novamente."
            : "O pagamento foi cancelado ou não foi concluído. Você pode tentar novamente quando quiser."
        }
        primaryLabel="Tentar novamente"
        primaryHref="/app/profile"
        secondaryLabel="Ir para meus painéis"
        secondaryHref="/app/boards"
      />
    );
  }

  if (!isAuthenticated) {
    if (path === "/app/signup") {
      return <SignupPage />;
    }
    if (path === "/app/forgot-password") {
      return <PasswordRecoveryPage />;
    }
    if (path === "/app/login") {
      return (
        <LoginScreen
          nextHref={url.searchParams.get("next") || "/app"}
          onLoggedIn={(nextHref) => {
            setIsAuthenticated(true);
            window.location.assign(nextHref);
          }}
        />
      );
    }
    if (path.startsWith("/app/") && !isKnownAppRoute) {
      return (
        <MiBoardStatusPage
          code="404"
          title="Página não encontrada"
          description="O endereço que você tentou abrir não existe ou foi removido."
          primaryLabel="Ir para a página principal"
          primaryHref="/"
          secondaryLabel="Entrar"
          secondaryHref={loginHrefFor(path)}
        />
      );
    }
    return (
      <LoginScreen
        nextHref={url.searchParams.get("next") || path}
        onLoggedIn={(nextHref) => {
          setIsAuthenticated(true);
          window.location.assign(nextHref);
        }}
      />
    );
  }

  if (path === "/app") {
    if (!window.location.search && !window.location.hash) {
      window.history.replaceState({}, APP_NAME, "/app?new=1");
    }
    return (
      <TopErrorBoundary>
        <Provider store={appJotaiStore}>
          <ExcalidrawAPIProvider>
            <ExcalidrawWrapper />
          </ExcalidrawAPIProvider>
        </Provider>
      </TopErrorBoundary>
    );
  }

  if (path === "/app/boards") {
    return (
      <TopErrorBoundary>
        <BoardList />
      </TopErrorBoundary>
    );
  }

  if (path === "/app/profile") {
    return (
      <TopErrorBoundary>
        <ProfilePage />
      </TopErrorBoundary>
    );
  }

  if (path.startsWith("/app/admin")) {
    if (currentRole === null) {
      return (
        <main className="miboard-loading-screen">
          <LoadingClock label="Carregando administração..." />
        </main>
      );
    }
    if (currentRole !== "admin") {
      return (
        <MiBoardStatusPage
          code="403"
          title="Acesso negado"
          description="Você não tem permissão para acessar esta área."
          primaryLabel="Ir para meus painéis"
          primaryHref="/app/boards"
          secondaryLabel="Abrir perfil"
          secondaryHref="/app/profile"
        />
      );
    }
    return (
      <TopErrorBoundary>
        <AdminPage />
      </TopErrorBoundary>
    );
  }

  if (path.startsWith("/app/")) {
    return (
      <MiBoardStatusPage
        code="404"
        title="Página não encontrada"
        description="Talvez o link que inseriu esteja desatualizado."
        primaryLabel="Ir para meus painéis"
        primaryHref="/app/boards"
        secondaryLabel="Abrir a landing page"
        secondaryHref="/"
      />
    );
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
